import {
  Box,
  Button,
  ButtonBase,
  Flex,
  Icon,
  InputAdornment,
  InputControl,
  Text,
  useColorMode,
  useColorStyle,
  Tooltip,
} from '@tonic-one/react';
import { LockIcon, ViewIcon, ViewOffIcon } from '@tonic-one/react-icons';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InlineError } from '@Components/Common/FormikComponent';
import { checkPassword, getValidateMsg } from '@Utils/common';
import { ValidProps } from '@Types/index';
import { validateIsNotEmptyString } from '@Utils/validate';
import { useNavigate } from 'react-router-dom';
import { ResetPasswordStep } from '../index';
import { AccountApi } from '@Apis';
import CSpinner from '@Components/CSpinner';

enum FontColor {
  success = 'color: #3c763d;',
  danger = 'color: #a94442;',
}

interface PwdFmtStatus {
  password_format_1: FontColor;
  password_format_2: FontColor;
  password_format_3: FontColor;
  password_format_4: FontColor;
  password_format_5: FontColor;
}

export const Reset = ({
  resetPasswordStep,
  setResetPasswordStep,
}: {
  resetPasswordStep: ResetPasswordStep;
  setResetPasswordStep: (value: ResetPasswordStep) => void;
}) => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const [password, setPassword] = useState<string>('');
  const [password2, setPassword2] = useState<string>('');
  const iconColor = colorStyle.color.tertiary;
  const navigate = useNavigate();
  const [authKey, setAuthKey] = useState<string>('');
  const { t } = useTranslation();
  const [errorMsg, setErrorMsg] = React.useState<string | undefined>(undefined);
  const [view, setView] = useState(false);
  const toggleView = () => setView((view) => !view);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    const searchParams = new URL(window.location.toString()).searchParams;
    const authKey = searchParams.get('auth_key') || '';
    setLoading(true);
    if (authKey && resetPasswordStep === ResetPasswordStep.Reset) {
      AccountApi.verifyAuthKey(authKey)
        .then((res) => {
          if (res.data.status === 0) {
            setAuthKey(authKey);
            console.log('auth success');
          } else {
            window.showNotification({
              titleMsg: t('login.password.reset.authFail'),
              severity: 'error',
            });
            navigate('/login');
            setResetPasswordStep(ResetPasswordStep.None);
          }
        })
        .catch((error) => {
          window.showNotification({
            titleMsg: t('login.password.reset.authFail'),
            severity: 'error',
          });
          console.error(error);
          navigate('/login');
          setResetPasswordStep(ResetPasswordStep.None);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      navigate('/login');
      setResetPasswordStep(ResetPasswordStep.None);
    }
  }, [navigate, t, setResetPasswordStep, resetPasswordStep]);
  const passwordValidates: ValidProps[] = [
    {
      validMethod: validateIsNotEmptyString,
      errMsg: t('login.password.validate.isEmpty'),
    },
    {
      validMethod: checkPassword,
      errMsg: t('login.password.validate.notMatchFormat'),
    },
  ];
  const validatePassword = (value: string) => {
    const msg = getValidateMsg(passwordValidates, value);
    console.log('validateUsername', msg);
    setErrorMsg(msg);
    return msg;
  };

  const restPassword = async () => {
    console.log('restPassword', password, password2);
    const msg = validatePassword(password2);
    if (msg !== undefined) {
      return;
    }
    if (password !== password2) {
      setErrorMsg(t('login.password.validate.notMatch'));
      return;
    }
    try {
      setLoading(true);
      const res = await AccountApi.resetPassword(password, authKey);
      if (res.data.status === 0) {
        window.showNotification({
          titleMsg: t('login.password.changedSuccess'),
          severity: 'info',
        });
        navigate('/login');
        setResetPasswordStep(ResetPasswordStep.None);
      } else {
        if (res.data.reason === 'LoginAccount;PASSWORD_SIMILAR_TO_CURRENT') {
          window.showNotification({
            titleMsg: t('login.resetPassword.passwordSimilar'),
            severity: 'error',
          });
        } else {
          window.showNotification({
            titleMsg: t('common.apiException.internalError'),
            severity: 'error',
          });
        }
      }
    } catch (error) {
      console.log(error);
      window.showNotification({
        titleMsg: t('common.apiException.internalError'),
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const [pwdFmtStatus, setPwdFmtStatus] = useState<PwdFmtStatus>({
    password_format_1: FontColor.danger,
    password_format_2: FontColor.danger,
    password_format_3: FontColor.danger,
    password_format_4: FontColor.danger,
    password_format_5: FontColor.danger,
  });

  useEffect(() => {
    setPwdFmtStatus({
      password_format_1:
        password.length >= 8 ? FontColor.success : FontColor.danger,
      password_format_2: /[A-Z]/.test(password)
        ? FontColor.success
        : FontColor.danger,
      password_format_3: /[a-z]/.test(password)
        ? FontColor.success
        : FontColor.danger,
      password_format_4: /[0-9]/.test(password)
        ? FontColor.success
        : FontColor.danger,
      password_format_5: /[~!@#$%^*-+=|{}[\]:;,.<>?]/.test(password)
        ? FontColor.success
        : FontColor.danger,
    });
  }, [password]);

  return (
    <>
      {loading && <CSpinner />}
      <Box width={493} p="40px 40px">
        <Flex alignItems="stretch" flexDirection="row">
          <Box flex="auto" color={colorStyle.color.primary}>
            <Text fontSize="lg" lineHeight="lg" mb={32}>
              {t('login.text.resetPassword')}
            </Text>
            <Box>
              <Tooltip
                label={
                  <Text
                    dangerouslySetInnerHTML={{
                      __html: t('login.text.resetPasswordRule', {
                        ...pwdFmtStatus,
                      }),
                    }}
                  />
                }
                PopperProps={{ usePortal: true }}
                backgroundColor={colorStyle.background.tertiary}
                color={colorStyle.color.primary}
                maxWidth={413}
                p="8px 12px"
              >
                <InputControl
                  border="1px solid"
                  onChange={(e: any) => {
                    setPassword(e.target.value);
                  }}
                  placeholder={t('login.password.text.newPassword')}
                  type={view ? 'text' : 'password'}
                  width={413}
                  startAdornment={
                    <InputAdornment>
                      <Icon as={LockIcon} color={iconColor} />
                    </InputAdornment>
                  }
                  endAdornment={
                    <InputAdornment>
                      <ButtonBase onClick={toggleView}>
                        {view ? (
                          <Icon as={ViewIcon} color={iconColor} />
                        ) : (
                          <Icon as={ViewOffIcon} color={iconColor} />
                        )}
                      </ButtonBase>
                    </InputAdornment>
                  }
                />
              </Tooltip>
            </Box>
            <Box>
              <InputControl
                border="1px solid"
                mt={16}
                onChange={(e: any) => {
                  validatePassword(e.target.value);
                  setPassword2(e.target.value);
                }}
                placeholder={t('login.password.text.confirmPassword')}
                type={view ? 'text' : 'password'}
                width={413}
                startAdornment={
                  <InputAdornment>
                    <Icon as={LockIcon} color={iconColor} />
                  </InputAdornment>
                }
                endAdornment={
                  <InputAdornment>
                    <ButtonBase onClick={toggleView}>
                      {view ? (
                        <Icon as={ViewIcon} color={iconColor} />
                      ) : (
                        <Icon as={ViewOffIcon} color={iconColor} />
                      )}
                    </ButtonBase>
                  </InputAdornment>
                }
              />
              <Box>
                {errorMsg !== undefined && (
                  <InlineError>{errorMsg}</InlineError>
                )}
              </Box>
            </Box>
            <Text>
              <Flex alignItems="stretch" justifyContent="flex-start" mt={22}>
                <Button
                  variant="primary"
                  onClick={restPassword}
                  width={201}
                  height={40}
                >
                  {t('login.text.resetPassword')}
                </Button>
                <Button
                  variant="secondary"
                  ml={8}
                  width={201}
                  height={40}
                  onClick={() => {
                    navigate('/login');
                    setResetPasswordStep(ResetPasswordStep.None);
                  }}
                >
                  {t('login.text.backToLogin')}
                </Button>
              </Flex>
            </Text>
          </Box>
        </Flex>
      </Box>
    </>
  );
};
